import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card, Row, Col, Badge, Button, Modal, Form } from "react-bootstrap";
import Spinner from "../generics/Spinner";
import moment from "moment";

const MAINTENANCE_TYPE_VARIANTS = {
  SCHEDULED: "primary",
  REPAIR: "warning",
  INSPECTION: "info",
  CLEANING: "secondary",
  REPLACEMENT: "dark",
  EMERGENCY: "danger",
  OTHER: "secondary",
};

const STATUS_VARIANTS = {
  SCHEDULED: "info",
  IN_PROGRESS: "warning",
  COMPLETED: "success",
  CANCELLED: "secondary",
};

const PHOTO_TYPE_LABELS = {
  BEFORE: "Before Maintenance",
  DURING: "During Maintenance",
  AFTER: "After Maintenance",
  ISSUE: "Issue Found",
  OTHER: "Other",
};

const MaintenanceRecordDetail = ({
  record,
  loading,
  error,
  onStart,
  onComplete,
  onCancel,
  onAddPhoto,
  onDeletePhoto,
}) => {
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [completeData, setCompleteData] = useState({
    description: "",
    findings: "",
    recommendations: "",
    cost: "",
    parts_replaced: "",
  });
  const [photoData, setPhotoData] = useState({
    photo: null,
    photo_type: "AFTER",
    caption: "",
  });

  if (loading && !record) {
    return <Spinner />;
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <i className="fa fa-exclamation-circle me-2"></i>
        Error loading record: {error.message || "Unknown error"}
      </div>
    );
  }

  if (!record) {
    return (
      <div className="alert alert-warning">
        Maintenance record not found
      </div>
    );
  }

  const handleComplete = () => {
    if (onComplete) {
      onComplete(record.id, completeData);
    }
    setShowCompleteModal(false);
  };

  const handleAddPhoto = () => {
    if (onAddPhoto && photoData.photo) {
      const formData = new FormData();
      formData.append("maintenance_record", record.id);
      formData.append("photo", photoData.photo);
      formData.append("photo_type", photoData.photo_type);
      formData.append("caption", photoData.caption);
      onAddPhoto(formData);
    }
    setShowPhotoModal(false);
    setPhotoData({ photo: null, photo_type: "AFTER", caption: "" });
  };

  return (
    <div className="maintenance-record-detail">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2>
            <i className="fa fa-clipboard me-2"></i>
            {record.title}
          </h2>
          <p className="text-muted mb-0">
            <Link to={`/maintenance/machines/${record.machine}`}>
              {record.machine_name}
            </Link>
            {" | "}
            {record.machine_location_name}
          </p>
        </div>
        <div>
          <Link to="/maintenance/records" className="btn btn-outline-secondary me-2">
            <i className="fa fa-arrow-left me-1"></i> Back
          </Link>
          {record.status === "SCHEDULED" && (
            <>
              <Button
                variant="warning"
                onClick={() => onStart && onStart(record.id)}
                className="me-2"
              >
                <i className="fa fa-play me-1"></i> Start
              </Button>
              <Link
                to={`/maintenance/records/${record.id}/edit`}
                className="btn btn-outline-primary me-2"
              >
                <i className="fa fa-pencil me-1"></i> Edit
              </Link>
            </>
          )}
          {record.status === "IN_PROGRESS" && (
            <Button
              variant="success"
              onClick={() => setShowCompleteModal(true)}
              className="me-2"
            >
              <i className="fa fa-check me-1"></i> Complete
            </Button>
          )}
          {(record.status === "SCHEDULED" || record.status === "IN_PROGRESS") && (
            <Button
              variant="outline-secondary"
              onClick={() => onCancel && onCancel(record.id)}
            >
              <i className="fa fa-times me-1"></i> Cancel
            </Button>
          )}
        </div>
      </div>

      <Row>
        {/* Main Info */}
        <Col md={8}>
          <Card className="mb-4">
            <Card.Header>
              <i className="fa fa-info-circle me-2"></i>
              Record Details
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <table className="table table-borderless table-sm">
                    <tbody>
                      <tr>
                        <td className="text-muted" style={{ width: "40%" }}>Status</td>
                        <td>
                          <Badge bg={STATUS_VARIANTS[record.status] || "secondary"}>
                            {record.status_display || record.status}
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Type</td>
                        <td>
                          <Badge bg={MAINTENANCE_TYPE_VARIANTS[record.maintenance_type] || "secondary"}>
                            {record.maintenance_type_display || record.maintenance_type}
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Machine</td>
                        <td>
                          <Link to={`/maintenance/machines/${record.machine}`}>
                            {record.machine_name}
                          </Link>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Location</td>
                        <td>{record.machine_location_name}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Service Provider</td>
                        <td>{record.service_provider_name || "-"}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Technician</td>
                        <td>
                          {record.performed_by_name || record.external_technician_name || "-"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Col>
                <Col md={6}>
                  <table className="table table-borderless table-sm">
                    <tbody>
                      <tr>
                        <td className="text-muted" style={{ width: "45%" }}>Scheduled Date</td>
                        <td>
                          {record.scheduled_date
                            ? moment(record.scheduled_date).format("MMM D, YYYY")
                            : "-"}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Started At</td>
                        <td>
                          {record.started_at
                            ? moment(record.started_at).format("MMM D, YYYY h:mm A")
                            : "-"}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Completed At</td>
                        <td>
                          {record.completed_at
                            ? moment(record.completed_at).format("MMM D, YYYY h:mm A")
                            : "-"}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Duration</td>
                        <td>
                          {record.duration_hours
                            ? `${record.duration_hours} hours`
                            : "-"}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Cost</td>
                        <td>
                          {record.cost
                            ? `$${parseFloat(record.cost).toLocaleString()}`
                            : "-"}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Created By</td>
                        <td>{record.created_by_name || "-"}</td>
                      </tr>
                    </tbody>
                  </table>
                </Col>
              </Row>

              {record.description && (
                <div className="mt-3">
                  <h6 className="text-muted">Description</h6>
                  <p>{record.description}</p>
                </div>
              )}

              {record.parts_replaced && (
                <div className="mt-3">
                  <h6 className="text-muted">Parts Replaced</h6>
                  <p>{record.parts_replaced}</p>
                </div>
              )}

              {record.findings && (
                <div className="mt-3">
                  <h6 className="text-muted">Findings</h6>
                  <p>{record.findings}</p>
                </div>
              )}

              {record.recommendations && (
                <div className="mt-3">
                  <h6 className="text-muted">Recommendations</h6>
                  <p>{record.recommendations}</p>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Photos */}
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>
                <i className="fa fa-camera me-2"></i>
                Photos
                <Badge bg="secondary" className="ms-2">
                  {record.photos?.length || 0}
                </Badge>
              </span>
              {record.status !== "CANCELLED" && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowPhotoModal(true)}
                >
                  <i className="fa fa-plus me-1"></i> Add Photo
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {!record.photos || record.photos.length === 0 ? (
                <p className="text-muted text-center mb-0">No photos attached</p>
              ) : (
                <Row>
                  {record.photos.map((photo) => (
                    <Col md={4} key={photo.id} className="mb-3">
                      <Card>
                        <Card.Img
                          variant="top"
                          src={photo.photo}
                          style={{ height: "150px", objectFit: "cover" }}
                        />
                        <Card.Body className="p-2">
                          <Badge bg="secondary" className="mb-1">
                            {PHOTO_TYPE_LABELS[photo.photo_type] || photo.photo_type}
                          </Badge>
                          {photo.caption && (
                            <p className="small mb-1">{photo.caption}</p>
                          )}
                          <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                              {moment(photo.uploaded_at).format("MMM D, YYYY")}
                            </small>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => onDeletePhoto && onDeletePhoto(photo.id)}
                            >
                              <i className="fa fa-trash"></i>
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Sidebar */}
        <Col md={4}>
          <Card className="mb-4">
            <Card.Header>
              <i className="fa fa-bolt me-2"></i>
              Quick Actions
            </Card.Header>
            <Card.Body className="d-grid gap-2">
              <Link
                to={`/maintenance/machines/${record.machine}`}
                className="btn btn-outline-primary"
              >
                <i className="fa fa-cog me-1"></i> View Machine
              </Link>
              {record.status === "SCHEDULED" && (
                <Button
                  variant="warning"
                  onClick={() => onStart && onStart(record.id)}
                >
                  <i className="fa fa-play me-1"></i> Start Maintenance
                </Button>
              )}
              {record.status === "IN_PROGRESS" && (
                <Button
                  variant="success"
                  onClick={() => setShowCompleteModal(true)}
                >
                  <i className="fa fa-check me-1"></i> Complete Maintenance
                </Button>
              )}
              <Button
                variant="outline-secondary"
                onClick={() => setShowPhotoModal(true)}
              >
                <i className="fa fa-camera me-1"></i> Add Photo
              </Button>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <i className="fa fa-clock-o me-2"></i>
              Timeline
            </Card.Header>
            <Card.Body>
              <div className="timeline">
                <div className="timeline-item">
                  <div className="timeline-marker bg-info"></div>
                  <div className="timeline-content">
                    <p className="mb-1"><strong>Created</strong></p>
                    <small className="text-muted">
                      {moment(record.created_at).format("MMM D, YYYY h:mm A")}
                    </small>
                  </div>
                </div>
                {record.scheduled_date && (
                  <div className="timeline-item">
                    <div className="timeline-marker bg-primary"></div>
                    <div className="timeline-content">
                      <p className="mb-1"><strong>Scheduled</strong></p>
                      <small className="text-muted">
                        {moment(record.scheduled_date).format("MMM D, YYYY")}
                      </small>
                    </div>
                  </div>
                )}
                {record.started_at && (
                  <div className="timeline-item">
                    <div className="timeline-marker bg-warning"></div>
                    <div className="timeline-content">
                      <p className="mb-1"><strong>Started</strong></p>
                      <small className="text-muted">
                        {moment(record.started_at).format("MMM D, YYYY h:mm A")}
                      </small>
                    </div>
                  </div>
                )}
                {record.completed_at && (
                  <div className="timeline-item">
                    <div className="timeline-marker bg-success"></div>
                    <div className="timeline-content">
                      <p className="mb-1"><strong>Completed</strong></p>
                      <small className="text-muted">
                        {moment(record.completed_at).format("MMM D, YYYY h:mm A")}
                      </small>
                    </div>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Complete Modal */}
      <Modal show={showCompleteModal} onHide={() => setShowCompleteModal(false)} dialogClassName="modal-logut" animation={false}>
        <Modal.Header closeButton>
          <Modal.Title>Complete Maintenance</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={completeData.description}
                onChange={(e) => setCompleteData({ ...completeData, description: e.target.value })}
                placeholder="What was done?"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Findings</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={completeData.findings}
                onChange={(e) => setCompleteData({ ...completeData, findings: e.target.value })}
                placeholder="What issues were found?"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Recommendations</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={completeData.recommendations}
                onChange={(e) => setCompleteData({ ...completeData, recommendations: e.target.value })}
                placeholder="Any recommendations?"
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cost ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={completeData.cost}
                    onChange={(e) => setCompleteData({ ...completeData, cost: e.target.value })}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Parts Replaced</Form.Label>
                  <Form.Control
                    type="text"
                    value={completeData.parts_replaced}
                    onChange={(e) => setCompleteData({ ...completeData, parts_replaced: e.target.value })}
                    placeholder="List any parts"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCompleteModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleComplete}>
            <i className="fa fa-check me-1"></i> Mark Complete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Photo Modal */}
      <Modal show={showPhotoModal} onHide={() => setShowPhotoModal(false)} dialogClassName="modal-logut" animation={false}>
        <Modal.Header closeButton>
          <Modal.Title>Add Photo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Photo *</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => setPhotoData({ ...photoData, photo: e.target.files[0] })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Photo Type</Form.Label>
              <Form.Select
                value={photoData.photo_type}
                onChange={(e) => setPhotoData({ ...photoData, photo_type: e.target.value })}
              >
                <option value="BEFORE">Before Maintenance</option>
                <option value="DURING">During Maintenance</option>
                <option value="AFTER">After Maintenance</option>
                <option value="ISSUE">Issue Found</option>
                <option value="OTHER">Other</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Caption</Form.Label>
              <Form.Control
                type="text"
                value={photoData.caption}
                onChange={(e) => setPhotoData({ ...photoData, caption: e.target.value })}
                placeholder="Describe the photo"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPhotoModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddPhoto} disabled={!photoData.photo}>
            <i className="fa fa-upload me-1"></i> Upload
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .timeline {
          position: relative;
          padding-left: 30px;
        }
        .timeline-item {
          position: relative;
          padding-bottom: 20px;
        }
        .timeline-item:last-child {
          padding-bottom: 0;
        }
        .timeline-item::before {
          content: '';
          position: absolute;
          left: -24px;
          top: 8px;
          height: calc(100% + 4px);
          width: 2px;
          background: #dee2e6;
        }
        .timeline-item:last-child::before {
          display: none;
        }
        .timeline-marker {
          position: absolute;
          left: -30px;
          top: 4px;
          width: 12px;
          height: 12px;
          border-radius: 50%;
        }
      `}</style>
    </div>
  );
};

export default MaintenanceRecordDetail;
